<template>
    <div v-if="interstitialsToShow.length" class="full-overlay" 
        @mouseenter="() => {this.showFullViewControls = true}" 
        @mouseleave="() => {this.showFullViewControls = false}">
        <div class="swiper-button swiper-prev-button" v-if="!isATouchingDevice && showFullViewControls && canGoPrev" 
            @click="goPrev">
            <div class="inner">
                <img class="forward-icon" src="/img/backward.png" />
            </div>
        </div>
        <swiper ref="swiper" @slideChangeTransitionEnd="onSlideChange">
            <swiper-slide v-for="interstitial in interstitialsToShow" :key="interstitial.id">
                <div class="interstitial-wrapper" :class="{ 'swiper-no-swiping': !isATouchingDevice}" :style="wrapperStyle">
                    <div class="interstitial-content" ref="interstitialContent">
                        <div class="close-button" @click="close(interstitial)">
                            <img src="/img/close_cross@2x.png" />
                        </div>
                        <a :href="interstitial.url" target="_blank" class="d-block" :style="{ cursor: interstitial.url ? 'pointer' : 'default' }">
                            <div v-if="interstitial.content_html" v-html="interstitial.content_html" class="text-container" :style="contentStyle"></div>
                            <div v-else-if="interstitial.image" class="img-container" :style="contentStyle">
                                <img v-if="interstitial.image?.preview_url" :src="interstitial.image.preview_url"/>
                                <img v-else :src="context.storageEndpoint + interstitial.image.file_name"/>
                            </div>
                        </a>
                    </div>
                </div>
            </swiper-slide>
        </swiper>
        <div class="swiper-button swiper-next-button" v-if="!isATouchingDevice && showFullViewControls && canGoNext"
            @click="goNext">
            <div class="inner">
                <img class="forward-icon" src="/img/forward.png" />
            </div>
        </div>
    </div>
</template>

<script>
import Cookies from 'js-cookie';
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';

export default {
    components: { Swiper, SwiperSlide },
    props: {
        context: { type: Object, required: true },
        interstitials: { type: Array, required: true },
        enableTemporarilyHidden: { type: Boolean, required: false, default: false },
    },
    data() {
        const isATouchingDevice = ('ontouchstart' in window);
        return {
            currentIndex: 0,
            showFullViewControls: false,
            isATouchingDevice,
            interstitialsToShow: [],
            windowInnerHeight: 0,
            idsOfInterstitialsToHide: [],
            idsOfInterstitialsToHideTemporarily: [],
        }
    },
    mounted() {
        this.prepareInterstitialToShow();
        this.updateWindowInnerHeight();
        window.addEventListener('resize', this.updateWindowInnerHeight);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateWindowInnerHeight);
    },
    watch: {
        interstitialsToShow() {
            const scrollElm = document.scrollingElement;
            if (this.interstitialsToShow.length) {
                scrollElm.classList.add('overflow-hidden');
            } else {
                scrollElm.classList.remove('overflow-hidden');
                this.$emit('close');
            }
        }
    },
    computed: {
        canGoNext() {
            return this.currentIndex < this.interstitialsToShow.length - 1;
        },
        canGoPrev() {
            return this.currentIndex > 0;
        },
        wrapperStyle() {
            return {height: this.windowInnerHeight + 'px'};
        },
        contentStyle() {
            return {maxHeight: (0.8 * this.windowInnerHeight) + 'px'};
        }
    },
    methods: {
        onSlideChange() {
            this.currentIndex = this.$refs['swiper'].swiperInstance.activeIndex;
        },
        goPrev() {
            this.$refs['swiper'].swiperInstance.slidePrev(300);
        },
        goNext() {
            this.$refs['swiper'].swiperInstance.slideNext(300);
        },
        goIndex(index) {
            this.$refs['swiper'].swiperInstance.slideTo(index);
        },
        close(interstitial) {
            this.interstitialsToShow = this.interstitialsToShow.filter(item => item.id !== interstitial.id);
            if (this.currentIndex > this.interstitialsToShow.length - 1 ) {
                this.currentIndex = this.interstitialsToShow.length - 1;
            }
            this.idsOfInterstitialsToHide.push(interstitial.id);
            this.idsOfInterstitialsToHideTemporarily.push(interstitial.id);
            localStorage.setItem('hidden_interstitial', this.idsOfInterstitialsToHide.join(','));
            if (this.enableTemporarilyHidden) {
                Cookies.set(
                    'temporarily_hidden_interstitials',
                    this.idsOfInterstitialsToHideTemporarily.join(','),
                    { path: '/', domain: window.location.hostname }
                );
            }
        },
        prepareInterstitialToShow() {
            this.loadIdsOfInterstitialsToHide();
            this.loadIdsOfInterstitialsToHideTemporarily();
            this.interstitialsToShow = this.interstitials.filter(interstitial => {
                return (interstitial.always || this.idsOfInterstitialsToHide.findIndex(item => interstitial.id == item) == -1)
                    && this.idsOfInterstitialsToHideTemporarily.findIndex(item => interstitial.id == item) == -1;
            });
        },
        updateWindowInnerHeight() {
            this.windowInnerHeight = window.innerHeight;
        },
        loadIdsOfInterstitialsToHide() {
            const ids = localStorage.getItem('hidden_interstitial');
            this.idsOfInterstitialsToHide = ids ? ids.split(',') : [];
        },
        loadIdsOfInterstitialsToHideTemporarily() {
            if (!this.enableTemporarilyHidden) {
                return [];
            }
            const ids = Cookies.get('temporarily_hidden_interstitials');
            this.idsOfInterstitialsToHideTemporarily = ids ? ids.split(',') : [];
        },
    }
}
</script>

<style lang="scss" scoped>
    .full-overlay {
        position: fixed;
        top: 0;
        right: 0;
        left: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 2147483647;
    }
    .interstitial-wrapper {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .interstitial-content {
        max-width: 720px;
        position: relative;
        margin: 16px;

        .text-container {
            background-color: #fff;
            padding: 24px;
            width: 80vw;
            max-width: 720px;
            overflow: auto;

            /deep/ p {
                margin: 0;
            }
        }

        .img-container {
            overflow: auto;
        }

        img {
            width: 100%;
            pointer-events: none;
        }

        a {
            color: black;
            text-decoration: none;
        }

        .close-button {
            width: 36px;
            height: 36px;
            border-radius: 22px;
            position: absolute;
            top: -44px;
            right: 1px;
            background-color: #666666cc;
            color: #F0F0F0;
            font-size: 16px;
            padding: 4px 8px;
            cursor: pointer;
            z-index: 1000;
            display: flex;
            align-items: center;
            justify-content: center;

            img {
                width: 18px;
                height: 18px;
                filter: brightness(0) invert(1);
                -webkit-filter: brightness(0) invert(1);
            }
        }
    }
    .swiper-prev-button {
        left: 0;
    }

    .swiper-next-button {
        right: 0;
    }

    .swiper-button {
        padding: 12px;
        z-index: 10;
        position: absolute;
        top: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        pointer-events: none;

        .inner {
            cursor: pointer;
            pointer-events: all;
            width: 44px;
            height: 44px;
            border-radius: 22px;
            background-color: #ffffff50;
            display: flex;
            align-items: center;
            justify-content: center;

            img {
                width: 25px;
                height: 25px;
            }
        }
    }

</style>